import { Link } from '@inertiajs/react'
import { excerpt, lengthLabel } from '@/utils/readingMeta'
import type { Reading } from '@/types/reading'
import { CefrBadge } from '@/components/ui/CefrBadge'
import { GrammarTags } from '@/components/ui/GrammarTags'

interface ReadingTextRowProps {
  reading: Reading
}

/**
 * One reading in the catalog (research D5/D6). The whole row is an Inertia link to the
 * reading. Source meta is shown only when present; excerpt is a plain-text preview (HTML
 * stripped, first sentences) clamped to two lines via CSS; the length hint is derived
 * client-side from the body.
 */
export function ReadingTextRow({ reading }: ReadingTextRowProps) {
  return (
    <Link
      href={`/readings/${reading.id}`}
      className="group grid grid-cols-[56px_1fr_auto] sm:grid-cols-[64px_1fr_auto] items-center gap-4 sm:gap-7 min-h-11 py-5 border-b border-line-soft text-body transition-colors duration-300 hover:text-em focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
    >
      <CefrBadge level={reading.difficulty_level} className="self-center justify-self-start" />

      <span className="min-w-0">
        <h3 className="font-serif text-lg sm:text-xl leading-snug transition-colors duration-200">
          {reading.title}
        </h3>
        {reading.source && (
          <span className="block mt-1 font-serif italic text-sm text-muted">{reading.source}</span>
        )}
        <span className="block mt-2 font-serif text-sm leading-relaxed text-muted [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden">
          {excerpt(reading.body)}
        </span>
        {reading.themes.length > 0 && (
          <span className="block mt-2.5">
            <GrammarTags tags={reading.themes.map((label) => ({ label }))} />
          </span>
        )}
      </span>

      <span className="flex items-center gap-3 sm:gap-5 self-center">
        <span className="hidden sm:inline font-mono text-[11.5px] uppercase tracking-[0.16em] text-muted text-right whitespace-nowrap">
          {lengthLabel(reading.body)}
        </span>
        <span
          aria-hidden="true"
          className="font-mono text-sm text-muted/60 transition-[color,opacity] duration-200 group-hover:text-accent group-hover:opacity-100"
        >
          →
        </span>
      </span>
    </Link>
  )
}
